import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  VoiceDictation,
  AmbientListeningSession,
  VoiceSettings,
  RecordingStatus,
  TranscriptionSegment,
  VoiceCommand,
  AudioVisualizationData,
} from '../types/voice';

interface VoiceStore {
  // State
  currentDictation: VoiceDictation | null;
  currentAmbientSession: AmbientListeningSession | null;
  dictationHistory: VoiceDictation[];
  ambientSessions: AmbientListeningSession[];
  settings: VoiceSettings;
  isRecording: boolean;
  recordingStatus: RecordingStatus;
  audioLevel: number;
  visualizationData: AudioVisualizationData | null;
  availableCommands: VoiceCommand[];
  lastCommand: VoiceCommand | null;

  // MediaRecorder and Audio Context
  mediaRecorder: MediaRecorder | null;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  audioChunks: Blob[];

  // Dictation Actions
  startDictation: (patientId?: string, encounterId?: string) => Promise<void>;
  pauseDictation: () => void;
  resumeDictation: () => void;
  stopDictation: () => Promise<void>;
  cancelDictation: () => void;

  // Transcription Actions
  updateTranscription: (segments: TranscriptionSegment[]) => void;
  editTranscriptionSegment: (segmentId: string, newText: string) => void;
  deleteTranscriptionSegment: (segmentId: string) => void;
  mergeTranscriptionSegments: (segmentIds: string[]) => void;

  // Ambient Listening Actions
  startAmbientListening: (patientId: string, encounterId: string) => Promise<void>;
  pauseAmbientListening: () => void;
  resumeAmbientListening: () => void;
  stopAmbientListening: () => Promise<void>;
  updateAmbientNotes: (notes: Partial<AmbientListeningSession['generatedNotes']>) => void;

  // Voice Command Actions
  executeVoiceCommand: (command: string) => Promise<void>;
  registerCommand: (command: VoiceCommand) => void;

  // Settings Actions
  updateSettings: (settings: Partial<VoiceSettings>) => void;

  // Audio Visualization
  updateAudioLevel: (level: number) => void;
  updateVisualizationData: (data: AudioVisualizationData) => void;

  // Utility Actions
  clearCurrentDictation: () => void;
  clearCurrentAmbientSession: () => void;
  setRecordingStatus: (status: RecordingStatus) => void;
  initializeAudioContext: () => Promise<void>;
  cleanupAudioContext: () => void;
  getAudioChunks: () => Blob[];
}

const defaultSettings: VoiceSettings = {
  language: 'en',
  sttLanguage: 'en',
  enableNoiseCancellation: true,
  enablePunctuation: true,
  enableMedicalVocabulary: true,
  enableAutoSave: true,
  confidenceThreshold: 0.8,
  maxRecordingDuration: 30,

  // Wake Word Detection (disabled by default, but client-side whisper enabled)
  enableWakeWord: false,
  useClientSideWhisper: true, // Enabled by default for faster transcription
  wakeWordMode: 'always-listening', // Default to always-listening for medical context
  pushToActivateKey: 'ctrl+space', // Default keyboard shortcut
  autoSendAfterTranscription: true, // Auto-send by default for convenience
  liveTranscription: false, // Advanced voice mode live partials — opt-in A/B (default off)
  defaultCaptureMode: 'visit', // Encounter recorder opens in Visit (diarized) by default
  preferRefinedForNote: false, // Speed-first by default: note off the live transcript, refined offered as an upgrade
  voiceViewMode: 'orb', // Advanced voice mode: centered orb by default (transcript is the alt view)
  // Push-to-talk by default: safer in a clinic — always-listening captures the
  // room (keyboard, ambient chatter) and triggers phantom turns. Continuous is
  // opt-in via the in-mode settings gear.
  voiceInputMode: 'push-to-talk',

  // TTS Settings
  enableTTS: true, // Enable TTS by default
  ttsVoice: 'af_heart', // Kokoro warm-female default (legacy VITS ids map to it server-side)
  ttsSpeed: 1.1, // Brisker default — 1.0 felt slow; Settings/voice-mode go up to 1.5
  ttsOnlyFloatingButton: true // TTS only active with floating button by default
};

const defaultCommands: VoiceCommand[] = [
  {
    command: 'start recording',
    action: 'startRecording',
    description: { en: 'Start voice recording', ar: 'بدء التسجيل الصوتي' },
    category: 'control'
  },
  {
    command: 'stop recording',
    action: 'stopRecording',
    description: { en: 'Stop voice recording', ar: 'إيقاف التسجيل الصوتي' },
    category: 'control'
  },
  {
    command: 'pause recording',
    action: 'pauseRecording',
    description: { en: 'Pause voice recording', ar: 'إيقاف مؤقت للتسجيل' },
    category: 'control'
  },
  {
    command: 'new paragraph',
    action: 'insertParagraph',
    description: { en: 'Insert new paragraph', ar: 'إدراج فقرة جديدة' },
    category: 'editing'
  },
  {
    command: 'next field',
    action: 'navigateNext',
    description: { en: 'Go to next field', ar: 'الانتقال إلى الحقل التالي' },
    category: 'navigation'
  },
  {
    command: 'previous field',
    action: 'navigatePrevious',
    description: { en: 'Go to previous field', ar: 'الانتقال إلى الحقل السابق' },
    category: 'navigation'
  }
];

export const useVoiceStore = create<VoiceStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentDictation: null,
      currentAmbientSession: null,
      dictationHistory: [],
      ambientSessions: [],
      settings: defaultSettings,
      isRecording: false,
      recordingStatus: 'idle',
      audioLevel: 0,
      visualizationData: null,
      availableCommands: defaultCommands,
      lastCommand: null,
      mediaRecorder: null,
      audioContext: null,
      analyser: null,
      audioChunks: [],

      // Initialize audio context
      initializeAudioContext: async () => {
        try {
          const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 2048;

          set({ audioContext, analyser });
        } catch (error) {
          console.error('Failed to initialize audio context:', error);
        }
      },

      // Cleanup audio context
      cleanupAudioContext: () => {
        const { audioContext, mediaRecorder } = get();

        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }

        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close();
        }

        set({ audioContext: null, analyser: null, mediaRecorder: null });
      },

      // Start dictation
      startDictation: async (patientId?: string, encounterId?: string) => {
        try {
          console.log('[VoiceStore] Starting dictation...');
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const mediaRecorder = new MediaRecorder(stream);
          console.log('[VoiceStore] MediaRecorder created, state:', mediaRecorder.state);

          const dictation: VoiceDictation = {
            id: Date.now().toString(),
            patientId,
            encounterId,
            status: 'recording',
            transcriptionStatus: 'pending',
            startTime: new Date(),
            duration: 0,
            transcription: [],
            language: get().settings.language,
            metadata: {}
          };

          mediaRecorder.ondataavailable = (event) => {
            // Collect audio data chunks
            if (event.data.size > 0) {
              console.log('[VoiceStore] Audio chunk received, size:', event.data.size);
              set(state => ({
                audioChunks: [...state.audioChunks, event.data]
              }));
            }
          };

          mediaRecorder.start(1000); // Collect data every second
          console.log('[VoiceStore] MediaRecorder started, collecting chunks every 1000ms');

          set({
            currentDictation: dictation,
            mediaRecorder,
            isRecording: true,
            recordingStatus: 'recording'
          });
          console.log('[VoiceStore] Dictation started successfully');
        } catch (error) {
          console.error('Failed to start dictation:', error);
          set({ recordingStatus: 'error' });
        }
      },

      // Pause dictation
      pauseDictation: () => {
        const { mediaRecorder } = get();
        if (mediaRecorder && mediaRecorder.state === 'recording') {
          mediaRecorder.pause();
          set({ recordingStatus: 'paused' });
        }
      },

      // Resume dictation
      resumeDictation: () => {
        const { mediaRecorder } = get();
        if (mediaRecorder && mediaRecorder.state === 'paused') {
          mediaRecorder.resume();
          set({ recordingStatus: 'recording' });
        }
      },

      // Stop dictation
      stopDictation: async () => {
        const { mediaRecorder, currentDictation, audioChunks } = get();
        console.log('[VoiceStore] Stopping dictation, audio chunks count:', audioChunks.length);

        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();

          // Stop all tracks
          mediaRecorder.stream.getTracks().forEach(track => track.stop());

          if (currentDictation) {
            const updatedDictation: VoiceDictation = {
              ...currentDictation,
              status: 'processing',
              endTime: new Date(),
              duration: Math.floor((new Date().getTime() - currentDictation.startTime.getTime()) / 1000)
            };

            set(state => ({
              dictationHistory: [...state.dictationHistory, updatedDictation],
              currentDictation: null,
              isRecording: false,
              recordingStatus: 'idle',
              mediaRecorder: null,
              audioChunks: [] // Clear audio chunks
            }));
          }
        }
      },

      // Cancel dictation
      cancelDictation: () => {
        const { mediaRecorder } = get();

        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }

        set({
          currentDictation: null,
          isRecording: false,
          recordingStatus: 'idle',
          mediaRecorder: null,
          audioChunks: [] // Clear audio chunks
        });
      },

      // Update transcription
      updateTranscription: (segments) => {
        set(state => ({
          currentDictation: state.currentDictation
            ? { ...state.currentDictation, transcription: segments }
            : null
        }));
      },

      // Edit transcription segment
      editTranscriptionSegment: (segmentId, newText) => {
        set(state => ({
          currentDictation: state.currentDictation
            ? {
              ...state.currentDictation,
              transcription: state.currentDictation.transcription.map(segment =>
                segment.id === segmentId
                  ? {
                    ...segment,
                    text: newText,
                    isEdited: true,
                    originalText: segment.originalText || segment.text
                  }
                  : segment
              )
            }
            : null
        }));
      },

      // Delete transcription segment
      deleteTranscriptionSegment: (segmentId) => {
        set(state => ({
          currentDictation: state.currentDictation
            ? {
              ...state.currentDictation,
              transcription: state.currentDictation.transcription.filter(
                segment => segment.id !== segmentId
              )
            }
            : null
        }));
      },

      // Merge transcription segments
      mergeTranscriptionSegments: (segmentIds) => {
        const { currentDictation } = get();
        if (!currentDictation) return;

        const segmentsToMerge = currentDictation.transcription.filter(s =>
          segmentIds.includes(s.id)
        );

        if (segmentsToMerge.length < 2) return;

        const mergedText = segmentsToMerge.map(s => s.text).join(' ');
        const mergedSegment: TranscriptionSegment = {
          id: Date.now().toString(),
          text: mergedText,
          timestamp: segmentsToMerge[0].timestamp,
          confidence: Math.min(...segmentsToMerge.map(s => s.confidence)),
          speaker: segmentsToMerge[0].speaker,
          isEdited: true
        };

        const newTranscription = currentDictation.transcription.filter(s =>
          !segmentIds.includes(s.id)
        );

        const insertIndex = currentDictation.transcription.findIndex(s =>
          s.id === segmentIds[0]
        );

        newTranscription.splice(insertIndex, 0, mergedSegment);

        set({
          currentDictation: {
            ...currentDictation,
            transcription: newTranscription
          }
        });
      },

      // Start ambient listening
      startAmbientListening: async (patientId, encounterId) => {
        const session: AmbientListeningSession = {
          id: Date.now().toString(),
          patientId,
          encounterId,
          startTime: new Date(),
          status: 'active',
          consent: {
            patientConsent: true,
            consentTimestamp: new Date(),
            consentMethod: 'electronic'
          },
          participants: [],
          recordingSegments: []
        };

        set({ currentAmbientSession: session });

        // Start recording
        await get().startDictation(patientId, encounterId);
      },

      // Pause ambient listening
      pauseAmbientListening: () => {
        set(state => ({
          currentAmbientSession: state.currentAmbientSession
            ? { ...state.currentAmbientSession, status: 'paused' }
            : null
        }));
        get().pauseDictation();
      },

      // Resume ambient listening
      resumeAmbientListening: () => {
        set(state => ({
          currentAmbientSession: state.currentAmbientSession
            ? { ...state.currentAmbientSession, status: 'active' }
            : null
        }));
        get().resumeDictation();
      },

      // Stop ambient listening
      stopAmbientListening: async () => {
        await get().stopDictation();

        set(state => ({
          currentAmbientSession: state.currentAmbientSession
            ? {
              ...state.currentAmbientSession,
              status: 'completed',
              endTime: new Date()
            }
            : null,
          ambientSessions: state.currentAmbientSession
            ? [...state.ambientSessions, {
              ...state.currentAmbientSession,
              status: 'completed' as const,
              endTime: new Date()
            }]
            : state.ambientSessions
        }));
      },

      // Update ambient notes
      updateAmbientNotes: (notes) => {
        set(state => ({
          currentAmbientSession: state.currentAmbientSession
            ? {
              ...state.currentAmbientSession,
              generatedNotes: {
                ...state.currentAmbientSession.generatedNotes,
                ...notes
              }
            }
            : null
        }));
      },

      // Execute voice command
      executeVoiceCommand: async (command) => {
        const availableCommand = get().availableCommands.find(
          cmd => cmd.command.toLowerCase() === command.toLowerCase()
        );

        if (availableCommand) {
          set({ lastCommand: availableCommand });

          // Execute the command action
          switch (availableCommand.action) {
            case 'startRecording':
              await get().startDictation();
              break;
            case 'stopRecording':
              await get().stopDictation();
              break;
            case 'pauseRecording':
              get().pauseDictation();
              break;
            // Add more command actions as needed
          }
        }
      },

      // Register command
      registerCommand: (command) => {
        set(state => ({
          availableCommands: [...state.availableCommands, command]
        }));
      },

      // Update settings
      updateSettings: (settings) => {
        set(state => ({
          settings: { ...state.settings, ...settings }
        }));
      },

      // Update audio level
      updateAudioLevel: (level) => {
        set({ audioLevel: level });
      },

      // Update visualization data
      updateVisualizationData: (data) => {
        set({ visualizationData: data });
      },

      // Clear current dictation
      clearCurrentDictation: () => {
        set({ currentDictation: null });
      },

      // Clear current ambient session
      clearCurrentAmbientSession: () => {
        set({ currentAmbientSession: null });
      },

      // Set recording status
      setRecordingStatus: (status) => {
        set({ recordingStatus: status });
      },

      // Get audio chunks
      getAudioChunks: () => {
        return get().audioChunks;
      }
    }),
    {
      name: 'voice-settings-storage',
      partialize: (state) => ({
        settings: state.settings, // Only persist settings, not runtime state
      }),
      // Deep-merge settings so new default fields (e.g. ttsOnlyFloatingButton)
      // aren't lost when loading old persisted state
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<VoiceStore> | undefined;
        return {
          ...currentState,
          settings: { ...currentState.settings, ...persisted?.settings },
        };
      },
    }
  )
);